import { Add, Button } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import AddSectionModal from './AddSectionModal'
import EolSectionCard from './EolSectionCard'
import type { EolStructuredSection } from './eolTypes'

interface EndOfLifeSectionsProps {
  sections: EolStructuredSection[]
  onChange: (sections: EolStructuredSection[]) => void
  isViewMode?: boolean
  isEolCarePlan?: boolean
}

const EndOfLifeSections: React.FC<EndOfLifeSectionsProps> = ({
  sections,
  onChange,
  isViewMode = false,
  isEolCarePlan = false
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const handleAddSection = (newSection: EolStructuredSection) => {
    if (editingIndex !== null) {
      const updated = [...sections]
      updated[editingIndex] = newSection
      onChange(updated)
      setEditingIndex(null)
    } else {
      onChange([...sections, newSection])
    }
  }

  const handleRemoveSection = (index: number) => {
    onChange(sections.filter((_, i) => i !== index))
  }

  const handleEditSection = (index: number) => {
    setEditingIndex(index)
    setIsModalVisible(true)
  }

  const handleModalClose = (visible: boolean) => {
    if (!visible) {
      setEditingIndex(null)
    }
    setIsModalVisible(visible)
  }

  return (
    <Wrapper data-testid="eol-sections-container">
      {sections.map((section, index) => (
        <EolSectionCard
          key={`${section.sectionType}-${index}`}
          section={section}
          isViewMode={isViewMode}
          index={index}
          onRemove={!isViewMode ? () => handleRemoveSection(index) : undefined}
          onEdit={!isViewMode ? () => handleEditSection(index) : undefined}
        />
      ))}
      {!isViewMode && (
        <AddSectionButton
          type="button"
          variant="text"
          icon={<Add />}
          onClick={() => setIsModalVisible(true)}
          data-testid="eol-add-new-section-btn"
        >
          {t(carePlansConstants.EOL_ADD_NEW_SECTION)}
        </AddSectionButton>
      )}
      {!isViewMode && (
        <AddSectionModal
          isModalVisible={isModalVisible}
          setIsModalVisible={handleModalClose}
          onSave={handleAddSection}
          existingSections={sections}
          isEolCarePlan={isEolCarePlan}
          editingSection={editingIndex !== null ? sections[editingIndex] : null}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const AddSectionButton = styled(Button)`
  width: 100%;
  justify-content: center;
  padding: 20px 0 !important;
  border: 1px dashed ${({ theme }) => theme.color.neutral.color6} !important;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  color: ${({ theme }) => theme.color.status.primary} !important;
  font-size: 15px;

  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color9};
  }
`

export default EndOfLifeSections
